import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  ClearAll as ClearAllIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface Deal {
  _id: string;
  asin: string;
  qdrantId: string;
  title: string; // Hebrew
  titleEnglish?: string; // English for reference
  brand?: string;
  price?: {
    displayAmount: string;
    amount: number;
    currency: string;
  };
  imageUrl?: string;
  features?: string[];
  description?: string; // Hebrew
  descriptionEnglish?: string; // English for reference
  material?: string;
  categories?: string[];
  shippingInfo?: string;
  summary?: string;
  isActive: boolean;
  lastUpdated: string;
  createdAt: string;
}

interface DealsStats {
  totalDeals: number;
  activeDeals: number;
  lastUpdated: string | null;
  qdrantCount: number;
}

interface CronStatus {
  morningJob: {
    running: boolean;
    nextDate: string | null;
  };
  eveningJob: {
    running: boolean;
    nextDate: string | null;
  };
}

export const DealsManagement: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalDeals, setTotalDeals] = useState(0);
  const [stats, setStats] = useState<DealsStats | null>(null);
  const [cronStatus, setCronStatus] = useState<CronStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Deal[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchDeals();
    fetchStats();
    fetchCronStatus();
  }, [page, rowsPerPage]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/deals?page=${page + 1}&limit=${rowsPerPage}`);
      if (response.data.success) {
        setDeals(response.data.data.deals);
        setTotalDeals(response.data.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to fetch deals');
      console.error('Error fetching deals:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/deals/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchCronStatus = async () => {
    try {
      const response = await api.get('/deals/cron/status');
      if (response.data.success) {
        setCronStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching cron status:', err);
    }
  };

  const handleDeleteDeal = async (deal: Deal) => {
    try {
      await api.delete(`/deals/${deal.asin}`);
      setDeals(deals.filter(d => d.asin !== deal.asin));
      setTotalDeals(totalDeals - 1);
      setDeleteDialogOpen(false);
      setSelectedDeal(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        // Check if this is a manual deal
        if (err.response?.data?.isManualDeal) {
          setError(err.response.data.error || 'This is a manual deal. Please delete it from the Manual Amazon Deals page.');
        } else {
          setError('Access denied: Super admin permissions required to delete deals');
        }
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in again');
      } else {
        setError(`Failed to delete deal: ${err.response?.data?.error || err.message}`);
      }
      console.error('Error deleting deal:', err);
      // Don't close the dialog if it's a manual deal so the user can see the error
      if (!err.response?.data?.isManualDeal) {
        setDeleteDialogOpen(false);
        setSelectedDeal(null);
      }
    }
  };

  const handleRefreshAll = async () => {
    try {
      setRefreshing(true);
      const response = await api.post('/deals/refresh');
      if (response.data.success) {
        await fetchDeals();
        await fetchStats();
        setError(null);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied: Super admin permissions required to refresh deals');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in again');
      } else {
        setError(`Failed to refresh deals: ${err.response?.data?.error || err.message}`);
      }
      console.error('Error refreshing deals:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleClearAllDeals = async () => {
    try {
      setClearing(true);
      const response = await api.delete('/deals');
      if (response.data.success) {
        setClearAllDialogOpen(false);
        setError(null);
        
        // Reset to page 0 and refresh stats
        // The useEffect will automatically fetch deals when page changes
        setPage(0);
        await fetchStats();
        
        // Show success message with preserved count if available
        if (response.data.data?.preservedManualDeals > 0) {
          console.log(`Cleared automated deals, preserved ${response.data.data.preservedManualDeals} manual deals`);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied: Super admin permissions required to clear deals');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in again');
      } else {
        setError(`Failed to clear all deals: ${err.response?.data?.error || err.message}`);
      }
      console.error('Error clearing all deals:', err);
    } finally {
      setClearing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await api.get(`/deals/search?query=${encodeURIComponent(searchQuery)}&limit=10`);
      if (response.data.success) {
        setSearchResults(response.data.data);
      }
    } catch (err) {
      setError('Search failed');
      console.error('Error searching deals:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCronAction = async (action: string) => {
    try {
      let endpoint = '';
      switch (action) {
        case 'start':
          endpoint = '/deals/cron/start';
          break;
        case 'stop':
          endpoint = '/deals/cron/stop';
          break;
        case 'trigger':
          endpoint = '/deals/cron/trigger';
          break;
      }

      if (endpoint) {
        await api.post(endpoint);
        await fetchCronStatus();
        if (action === 'trigger') {
          await fetchDeals();
          await fetchStats();
        }
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied: Super admin permissions required for this operation');
      } else if (err.response?.status === 401) {
        setError('Authentication required: Please log in again');
      } else {
        setError(`Failed to ${action} cron job: ${err.response?.data?.error || err.message}`);
      }
      console.error(`Error with cron action ${action}:`, err);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, deal: Deal) => {
    setAnchorEl(event.currentTarget);
    setSelectedDeal(deal);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDeal(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price: Deal['price']) => {
    if (!price) return 'N/A';
    return `${price.displayAmount} ${price.currency}`;
  };

  const getAmazonUrl = (asin: string) => {
    return `https://www.amazon.com/dp/${asin}`;
  };

  const openAmazonPage = (asin: string) => {
    window.open(getAmazonUrl(asin), '_blank', 'noopener,noreferrer');
  };

  const displayDeals = searchQuery ? searchResults : deals;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Cricut Deals Management
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Deals
                </Typography>
                <Typography variant="h5">
                  {stats.totalDeals}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Deals
                </Typography>
                <Typography variant="h5">
                  {stats.activeDeals}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Qdrant Count
                </Typography>
                <Typography variant="h5">
                  {stats.qdrantCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="h6">
                  {stats.lastUpdated ? formatDate(stats.lastUpdated) : 'Never'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Cron Status and Controls */}
      {cronStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cron Job Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="Morning Job"
                    color={cronStatus.morningJob.running ? 'success' : 'default'}
                    size="small"
                  />
                  <Typography variant="body2">
                    Next: {cronStatus.morningJob.nextDate ? formatDate(cronStatus.morningJob.nextDate) : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="Evening Job"
                    color={cronStatus.eveningJob.running ? 'success' : 'default'}
                    size="small"
                  />
                  <Typography variant="body2">
                    Next: {cronStatus.eveningJob.nextDate ? formatDate(cronStatus.eveningJob.nextDate) : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => handleCronAction('start')}
                size="small"
              >
                Start Jobs
              </Button>
              <Button
                variant="outlined"
                startIcon={<StopIcon />}
                onClick={() => handleCronAction('stop')}
                size="small"
              >
                Stop Jobs
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => handleCronAction('trigger')}
                size="small"
              >
                Trigger Manual Refresh
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Search deals"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={isSearching}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </Button>
        <Button
          variant="contained"
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleRefreshAll}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh All Deals'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={clearing ? <CircularProgress size={16} /> : <ClearAllIcon />}
          onClick={() => setClearAllDialogOpen(true)}
          disabled={clearing || totalDeals === 0}
        >
          {clearing ? 'Clearing...' : 'Clear All Deals'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Deals Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Categories</TableCell>
              <TableCell>Shipping</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : displayDeals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No deals found
                </TableCell>
              </TableRow>
            ) : (
              displayDeals.map((deal) => (
                <TableRow key={deal._id}>
                  <TableCell>
                    {deal.imageUrl ? (
                      <img
                        src={deal.imageUrl}
                        alt={deal.title}
                        style={{ width: 50, height: 50, objectFit: 'cover' }}
                      />
                    ) : (
                      <ShoppingCartIcon />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        direction: 'rtl',
                        textAlign: 'right'
                      }}
                    >
                      {deal.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ASIN: {deal.asin}
                    </Typography>
                  </TableCell>
                  <TableCell>{deal.brand || 'N/A'}</TableCell>
                  <TableCell>{formatPrice(deal.price)}</TableCell>
                  <TableCell>
                    {deal.categories && deal.categories.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {deal.categories.slice(0, 2).map((category, index) => (
                          <Chip key={index} label={category} size="small" />
                        ))}
                        {deal.categories.length > 2 && (
                          <Chip label={`+${deal.categories.length - 2}`} size="small" />
                        )}
                      </Box>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {deal.shippingInfo || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(deal.lastUpdated)}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, deal)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!searchQuery && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalDeals}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setDetailDialogOpen(true);
          setAnchorEl(null); // Close menu but keep selectedDeal
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {selectedDeal && (
          <MenuItem onClick={() => {
            openAmazonPage(selectedDeal.asin);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View on Amazon</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setDeleteDialogOpen(true);
          setAnchorEl(null); // Close menu but keep selectedDeal
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Deal</ListItemText>
        </MenuItem>
      </Menu>

      {/* Deal Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedDeal(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ direction: selectedDeal?.title ? 'rtl' : 'ltr', textAlign: selectedDeal?.title ? 'right' : 'left' }}>
          {selectedDeal?.title || 'Deal Details'}
        </DialogTitle>
        <DialogContent>
          {selectedDeal ? (
              <Box sx={{ mb: 2 }}>
                {selectedDeal.imageUrl && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img
                      src={selectedDeal.imageUrl}
                      alt={selectedDeal.title}
                      style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                    />
                  </Box>
                )}
                
                {/* Hebrew Description */}
                {selectedDeal.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      תיאור (Description):
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        direction: 'rtl', 
                        textAlign: 'right',
                        mb: 2,
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {selectedDeal.description}
                    </Typography>
                  </Box>
                )}

                {/* English Reference */}
                {(selectedDeal.titleEnglish || selectedDeal.descriptionEnglish) && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      English (for reference):
                    </Typography>
                    {selectedDeal.titleEnglish && (
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {selectedDeal.titleEnglish}
                      </Typography>
                    )}
                    {selectedDeal.descriptionEnglish && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedDeal.descriptionEnglish}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Additional Details */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">ASIN:</Typography>
                    <Typography variant="body2">{selectedDeal.asin}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Brand:</Typography>
                    <Typography variant="body2">{selectedDeal.brand || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Price:</Typography>
                    <Typography variant="body2">{formatPrice(selectedDeal.price)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Shipping:</Typography>
                    <Typography variant="body2">{selectedDeal.shippingInfo || 'N/A'}</Typography>
                  </Grid>
                  {selectedDeal.features && selectedDeal.features.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Features:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {selectedDeal.features.map((feature, index) => (
                          <Chip key={index} label={feature} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {selectedDeal.categories && selectedDeal.categories.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Categories:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {selectedDeal.categories.map((category, index) => (
                          <Chip key={index} label={category} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {selectedDeal.summary && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Summary:</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedDeal.summary}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
          ) : (
            <Typography>No deal selected</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {selectedDeal && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => openAmazonPage(selectedDeal.asin)}
              variant="outlined"
            >
              View on Amazon
            </Button>
          )}
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedDeal(null);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => {
        setDeleteDialogOpen(false);
        setSelectedDeal(null);
      }}>
        <DialogTitle>Delete Deal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this deal? This action cannot be undone.
          </Typography>
          {selectedDeal && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, direction: 'rtl', textAlign: 'right' }}>
              {selectedDeal.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setSelectedDeal(null);
          }}>Cancel</Button>
          <Button
            onClick={() => {
              if (selectedDeal) {
                handleDeleteDeal(selectedDeal);
              }
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Deals Confirmation Dialog */}
      <Dialog open={clearAllDialogOpen} onClose={() => setClearAllDialogOpen(false)}>
        <DialogTitle color="error">Clear All Deals</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Warning: This will permanently delete ALL deals from both the database and Qdrant.
            </Typography>
          </Alert>
          <Typography>
            Are you absolutely sure you want to clear all {totalDeals} deals? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            All deals will be removed from:
          </Typography>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>MongoDB database</li>
            <li>Qdrant vector database</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleClearAllDeals}
            color="error"
            variant="contained"
            disabled={clearing}
            startIcon={clearing ? <CircularProgress size={16} /> : <ClearAllIcon />}
          >
            {clearing ? 'Clearing...' : 'Clear All Deals'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
